import React, { useMemo } from "react";
import styles from "./StudentTable.module.css";
import type { ColumnDef, tableProps } from "@/components/common/table/types.props";
import type { user } from "@/types/api/user.api";
import { Table } from "@/components/common/table/Table";

const STATUS_CLASS_MAP: Record<string, string> = {
    active: styles["badge-active"],
    inactive: styles["badge-inactive"],
};

const ROLE_CLASS_MAP: Record<string, string> = {
    Facilitador: styles["badge-facilitator"],
    Estudiante: styles["badge-student"],
    "Coordinador de curso": styles["badge-coordinator"],
};

const StatusBadge = ({ status }: { status: string }) => (
    <span
        className={`${styles["badge"]} ${STATUS_CLASS_MAP[status] || styles["badge-inactive"]}`}
    >
        {status}
    </span>
);

const RoleBadge = ({ role }: { role: string }) => (
    <span
        className={`${styles["badge"]} ${ROLE_CLASS_MAP[role] || styles["badge-student"]}`}
    >
        {role}
    </span>
);

type studentTableProps = Omit<tableProps<user>, "columns">;

const StudentTable: React.FC<studentTableProps> = ({
    data,
    isLoading,
    actions,
}) => {
    const columns = useMemo<ColumnDef<user>[]>(
        () => [
            {
                accessorKey: "identifier",
                header: "Identificador",
                cell: (user) => user.identifier,
            },
            {
                accessorKey: "first_name",
                header: "Nombre",
                cell: (user) => `${user.first_name} ${user.last_name}`,
            },
            {
                accessorKey: "email",
                header: "Correo",
                cell: (user) => user.email,
            },
            {
                accessorKey: "status",
                header: "Estado",
                cell: (user) => (
                    <StatusBadge status={user.status as "active" | "inactive"} />
                ),
            },
            {
                accessorKey: "role",
                header: "Rol",
                cell: (user) => <RoleBadge role={user.role} />,
            },
            {
                accessorKey: "institution",
                header: "Institución",
                cell: (user) => (user.institution ? user.institution.name : "N/A"),
            },
        ],
        [],
    );

    return (
        <Table<user>
            columns={columns}
            data={data}
            isLoading={isLoading}
            actions={actions}
        />
    );
};

export default StudentTable;
